if (!customElements.get("rdp-swashaa-hero")) {
  customElements.define(
    "rdp-swashaa-hero",
    class RdpSwashaaHero extends HTMLElement {
      connectedCallback() {
        if (this.dataset.rdpReady === "true") return;

        this.dataset.rdpReady = "true";

        this.viewport = this.querySelector("[data-viewport]");
        this.track = this.querySelector("[data-track]");

        this.originalSlides = Array.from(
          this.querySelectorAll("[data-slide]"),
        );

        this.dots = Array.from(
          this.querySelectorAll("[data-dot]"),
        );

        this.count = this.originalSlides.length;

        if (
          !this.viewport ||
          !this.track ||
          !this.count
        ) {
          return;
        }

        const initialIndex = Math.max(
          0,
          this.originalSlides.findIndex(
            (slide) =>
              slide.getAttribute("aria-hidden") ===
              "false",
          ),
        );

        this.speed =
          Number(this.dataset.speed) || 5000;

        this.reducedMotion = matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        this.canAutoplay =
          this.dataset.autoplay === "true" &&
          this.count > 1 &&
          !this.reducedMotion;

        this.visible = false;
        this.animating = false;
        this.startX = 0;
        this.currentX = 0;
        this.dragging = false;
        this.pointerDragging = false;
        this.pointerMoved = false;
        this.pointerId = null;
        this.dragOriginX = 0;
        this.suppressClick = false;

        /*
         * Three sets are used:
         *
         * Previous clones
         * Original slides
         * Next clones
         *
         * This makes the final slide visible to the
         * left of slide one and provides a seamless loop.
         */
        if (this.count > 1) {
          this.createInfiniteLoop();

          this.index =
            this.count + initialIndex;
        } else {
          this.slides =
            this.originalSlides;

          this.index = initialIndex;
        }

        this.logicalIndex = initialIndex;

        this.querySelector(
          "[data-prev]",
        )?.addEventListener(
          "click",
          this.previous,
        );

        this.querySelector(
          "[data-next]",
        )?.addEventListener(
          "click",
          this.next,
        );

        this.dots.forEach((dot) => {
          dot.addEventListener(
            "click",
            this.onDotClick,
          );
        });

        this.track.addEventListener(
          "transitionend",
          this.onTransitionEnd,
        );

        this.track.addEventListener(
          "touchstart",
          this.onTouchStart,
          {
            passive: true,
          },
        );

        this.track.addEventListener(
          "touchmove",
          this.onTouchMove,
          {
            passive: true,
          },
        );

        this.track.addEventListener(
          "touchend",
          this.onTouchEnd,
          {
            passive: true,
          },
        );

        this.viewport.addEventListener(
          "pointerdown",
          this.onPointerDown,
        );

        this.viewport.addEventListener(
          "pointermove",
          this.onPointerMove,
          { passive: false },
        );

        this.viewport.addEventListener(
          "pointerup",
          this.onPointerUp,
        );

        this.viewport.addEventListener(
          "pointercancel",
          this.onPointerCancel,
        );

        this.viewport.addEventListener(
          "click",
          this.onPointerClick,
          true,
        );

        this.addEventListener(
          "mouseenter",
          this.stop,
        );

        this.addEventListener(
          "mouseleave",
          this.start,
        );

        this.addEventListener(
          "focusin",
          this.stop,
        );

        this.addEventListener(
          "focusout",
          this.start,
        );

        document.addEventListener(
          "visibilitychange",
          this.onVisibilityChange,
        );

        this.resizeObserver =
          new ResizeObserver(
            this.onResize,
          );

        this.resizeObserver.observe(
          this.viewport,
        );

        if (this.slides[0]) {
          this.resizeObserver.observe(
            this.slides[0],
          );
        }

        this.intersectionObserver =
          new IntersectionObserver(
            this.onIntersection,
            {
              threshold: 0.2,
            },
          );

        this.intersectionObserver.observe(
          this,
        );

        if (window.Shopify?.designMode) {
          document.addEventListener(
            "shopify:block:select",
            this.onBlockSelect,
          );
        }

        this.update(false);
      }

      disconnectedCallback() {
        this.stop();

        clearTimeout(
          this.transitionTimer,
        );

        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();

        this.querySelector(
          "[data-prev]",
        )?.removeEventListener(
          "click",
          this.previous,
        );

        this.querySelector(
          "[data-next]",
        )?.removeEventListener(
          "click",
          this.next,
        );

        this.dots.forEach((dot) => {
          dot.removeEventListener(
            "click",
            this.onDotClick,
          );
        });

        this.track?.removeEventListener(
          "transitionend",
          this.onTransitionEnd,
        );

        this.track?.removeEventListener(
          "touchstart",
          this.onTouchStart,
        );

        this.track?.removeEventListener(
          "touchmove",
          this.onTouchMove,
        );

        this.track?.removeEventListener(
          "touchend",
          this.onTouchEnd,
        );

        this.viewport?.removeEventListener(
          "pointerdown",
          this.onPointerDown,
        );

        this.viewport?.removeEventListener(
          "pointermove",
          this.onPointerMove,
        );

        this.viewport?.removeEventListener(
          "pointerup",
          this.onPointerUp,
        );

        this.viewport?.removeEventListener(
          "pointercancel",
          this.onPointerCancel,
        );

        this.viewport?.removeEventListener(
          "click",
          this.onPointerClick,
          true,
        );

        this.removeEventListener(
          "mouseenter",
          this.stop,
        );

        this.removeEventListener(
          "mouseleave",
          this.start,
        );

        this.removeEventListener(
          "focusin",
          this.stop,
        );

        this.removeEventListener(
          "focusout",
          this.start,
        );

        document.removeEventListener(
          "visibilitychange",
          this.onVisibilityChange,
        );

        document.removeEventListener(
          "shopify:block:select",
          this.onBlockSelect,
        );
      }

      createInfiniteLoop() {
        const before =
          document.createDocumentFragment();

        const after =
          document.createDocumentFragment();

        this.originalSlides.forEach(
          (slide) => {
            before.appendChild(
              this.cloneSlide(slide),
            );
          },
        );

        this.originalSlides.forEach(
          (slide) => {
            after.appendChild(
              this.cloneSlide(slide),
            );
          },
        );

        this.track.insertBefore(
          before,
          this.track.firstChild,
        );

        this.track.appendChild(after);

        this.slides = Array.from(
          this.track.querySelectorAll(
            "[data-slide]",
          ),
        );
      }

      cloneSlide(slide) {
        const clone =
          slide.cloneNode(true);

        clone.dataset.rdpClone = "true";

        clone.setAttribute(
          "aria-hidden",
          "true",
        );

        clone.removeAttribute(
          "data-shopify-editor-block",
        );

        clone
          .querySelectorAll(
            "[data-shopify-editor-block]",
          )
          .forEach((element) => {
            element.removeAttribute(
              "data-shopify-editor-block",
            );
          });

        clone
          .querySelectorAll(
            "a, button, input, select, textarea",
          )
          .forEach((element) => {
            element.setAttribute(
              "tabindex",
              "-1",
            );
          });

        return clone;
      }

      getLogicalIndex(
        index = this.index,
      ) {
        if (this.count < 2) {
          return index;
        }

        return (
          ((index - this.count) %
            this.count) +
          this.count
        ) % this.count;
      }

      updateState() {
        this.logicalIndex =
          this.getLogicalIndex();

        this.slides.forEach(
          (slide, slideIndex) => {
            const active =
              slideIndex === this.index;

            slide.setAttribute(
              "aria-hidden",
              String(!active),
            );

            slide
              .querySelectorAll(
                "[data-slide-link]",
              )
              .forEach((link) => {
                if (active) {
                  link.removeAttribute(
                    "tabindex",
                  );
                } else {
                  link.setAttribute(
                    "tabindex",
                    "-1",
                  );
                }
              });
          },
        );

        this.dots.forEach(
          (dot, dotIndex) => {
            dot.setAttribute(
              "aria-current",
              String(
                dotIndex ===
                  this.logicalIndex,
              ),
            );
          },
        );
      }

      updatePosition = (
        animate = true,
      ) => {
        const activeSlide =
          this.slides[this.index];

        if (
          !this.viewport ||
          !this.track ||
          !activeSlide
        ) {
          return;
        }

        if (
          !animate ||
          this.reducedMotion
        ) {
          this.track.dataset.noTransition =
            "true";
        }

        const viewportCenter =
          this.viewport.clientWidth / 2;

        const slideCenter =
          activeSlide.offsetLeft +
          activeSlide.offsetWidth / 2;

        const target =
          viewportCenter - slideCenter;

        this.track.style.transform =
          `translate3d(${target}px, 0, 0)`;

        if (
          !animate ||
          this.reducedMotion
        ) {
          void this.track.offsetHeight;

          requestAnimationFrame(() => {
            this.track.dataset.noTransition =
              "false";
          });
        }
      };

      update(animate = true) {
        this.updateState();
        this.updatePosition(animate);
      }

      move(step, restart = true) {
        if (
          this.count < 2 ||
          this.animating
        ) {
          return;
        }

        this.animating = true;
        this.index += step;

        this.update(true);

        if (restart) {
          this.restart();
        }

        clearTimeout(
          this.transitionTimer,
        );

        this.transitionTimer =
          setTimeout(() => {
            this.normalizeIndex();
          }, 760);
      }

      showLogical(
        logicalIndex,
        restart = true,
      ) {
        if (!this.count) return;

        const normalized =
          ((logicalIndex %
            this.count) +
            this.count) %
          this.count;

        if (this.count < 2) {
          this.index = normalized;
          this.update(false);
          return;
        }

        const base =
          this.count + normalized;

        const candidates = [
          base - this.count,
          base,
          base + this.count,
        ];

        let target = candidates[0];

        candidates.forEach(
          (candidate) => {
            const candidateDistance =
              Math.abs(
                candidate -
                  this.index,
              );

            const currentDistance =
              Math.abs(
                target -
                  this.index,
              );

            if (
              candidateDistance <
              currentDistance
            ) {
              target = candidate;
            }
          },
        );

        if (target === this.index) {
          if (restart) {
            this.restart();
          }

          return;
        }

        this.animating = true;
        this.index = target;

        this.update(true);

        if (restart) {
          this.restart();
        }

        clearTimeout(
          this.transitionTimer,
        );

        this.transitionTimer =
          setTimeout(() => {
            this.normalizeIndex();
          }, 760);
      }

      normalizeIndex() {
        if (this.count < 2) {
          this.animating = false;
          return;
        }

        let normalized = false;

        if (
          this.index >=
          this.count * 2
        ) {
          this.index -= this.count;
          normalized = true;
        } else if (
          this.index < this.count
        ) {
          this.index += this.count;
          normalized = true;
        }

        if (normalized) {
          this.update(false);
        }

        this.animating = false;

        clearTimeout(
          this.transitionTimer,
        );
      }

      previous = () => {
        this.move(-1, true);
      };

      next = () => {
        this.move(1, true);
      };

      onDotClick = (event) => {
        this.showLogical(
          Number(
            event.currentTarget
              .dataset.dot,
          ),
          true,
        );
      };

      onTransitionEnd = (
        event,
      ) => {
        if (
          event.target !== this.track ||
          event.propertyName !==
            "transform"
        ) {
          return;
        }

        this.normalizeIndex();
      };

      onResize = () => {
        this.updatePosition(false);
      };

      onTouchStart = (event) => {
        this.startX =
          event.touches[0].clientX;

        this.currentX =
          this.startX;

        this.dragging = true;

        this.stop();
      };

      onTouchMove = (event) => {
        if (!this.dragging) return;

        this.currentX =
          event.touches[0].clientX;
      };

      onTouchEnd = () => {
        if (!this.dragging) return;

        this.dragging = false;

        const difference =
          this.startX -
          this.currentX;

        if (
          Math.abs(difference) > 50
        ) {
          this.move(
            difference > 0
              ? 1
              : -1,
            false,
          );
        } else {
          this.updatePosition(true);
        }

        this.startX = 0;
        this.currentX = 0;

        this.start();
      };

      getTrackTranslateX() {
        const transform =
          getComputedStyle(
            this.track,
          ).transform;

        if (
          !transform ||
          transform === "none"
        ) {
          return 0;
        }

        try {
          return new DOMMatrixReadOnly(
            transform,
          ).m41;
        } catch (error) {
          const match = transform.match(
            /matrix(?:3d)?\((.+)\)/,
          );

          if (!match) return 0;

          const values = match[1]
            .split(",")
            .map(Number);

          return transform.startsWith(
            "matrix3d",
          )
            ? values[12] || 0
            : values[4] || 0;
        }
      }

      onPointerDown = (event) => {
        if (
          this.count < 2 ||
          this.animating ||
          (event.pointerType !== "mouse" &&
            event.pointerType !== "pen") ||
          event.button !== 0
        ) {
          return;
        }

        this.pointerDragging = true;
        this.pointerMoved = false;
        this.pointerId = event.pointerId;
        this.startX = event.clientX;
        this.currentX = event.clientX;
        this.dragOriginX =
          this.getTrackTranslateX();

        this.track.dataset.noTransition =
          "true";

        this.classList.add(
          "rdp-swashaa-hero--dragging",
        );

        this.viewport.setPointerCapture?.(
          event.pointerId,
        );

        this.stop();
      };

      onPointerMove = (event) => {
        if (
          !this.pointerDragging ||
          event.pointerId !==
            this.pointerId
        ) {
          return;
        }

        this.currentX = event.clientX;

        const delta =
          this.currentX - this.startX;

        if (Math.abs(delta) > 4) {
          this.pointerMoved = true;
          event.preventDefault();
        }

        this.track.style.transform =
          `translate3d(${this.dragOriginX + delta}px, 0, 0)`;
      };

      finishPointerDrag(
        event,
        cancelled = false,
      ) {
        if (
          !this.pointerDragging ||
          event.pointerId !==
            this.pointerId
        ) {
          return;
        }

        const delta =
          this.currentX - this.startX;

        if (
          this.viewport.hasPointerCapture?.(
            event.pointerId,
          )
        ) {
          this.viewport.releasePointerCapture(
            event.pointerId,
          );
        }

        this.pointerDragging = false;
        this.pointerId = null;

        this.classList.remove(
          "rdp-swashaa-hero--dragging",
        );

        this.track.dataset.noTransition =
          "false";

        if (
          !cancelled &&
          Math.abs(delta) > 50
        ) {
          this.animating = true;
          this.index += delta < 0 ? 1 : -1;
          this.update(true);

          clearTimeout(
            this.transitionTimer,
          );

          this.transitionTimer =
            setTimeout(() => {
              this.normalizeIndex();
            }, 760);
        } else {
          this.updatePosition(true);
        }

        if (this.pointerMoved) {
          this.suppressClick = true;

          setTimeout(() => {
            this.suppressClick = false;
          }, 0);
        }

        this.startX = 0;
        this.currentX = 0;
        this.dragOriginX = 0;
        this.pointerMoved = false;

        if (!this.matches(":hover")) {
          this.start();
        }
      }

      onPointerUp = (event) => {
        this.finishPointerDrag(
          event,
          false,
        );
      };

      onPointerCancel = (event) => {
        this.finishPointerDrag(
          event,
          true,
        );
      };

      onPointerClick = (event) => {
        if (!this.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      };

      onIntersection = ([entry]) => {
        this.visible =
          entry.isIntersecting;

        if (this.visible) {
          this.start();
        } else {
          this.stop();
        }
      };

      onVisibilityChange = () => {
        if (document.hidden) {
          this.stop();
        } else {
          this.start();
        }
      };

      onBlockSelect = (event) => {
        const slide =
          event.target.closest(
            "[data-slide]",
          );

        if (
          !slide ||
          !this.contains(slide)
        ) {
          return;
        }

        const logicalIndex =
          Number(
            slide.dataset
              .originalIndex,
          );

        if (
          !Number.isFinite(
            logicalIndex,
          )
        ) {
          return;
        }

        this.stop();

        this.index =
          this.count > 1
            ? this.count +
              logicalIndex
            : logicalIndex;

        this.update(false);
      };

      start = () => {
        if (
          !this.canAutoplay ||
          !this.visible ||
          this.timer ||
          document.hidden
        ) {
          return;
        }

        this.timer = setInterval(
          () => {
            this.move(1, false);
          },
          this.speed,
        );
      };

      stop = () => {
        clearInterval(this.timer);
        this.timer = null;
      };

      restart() {
        this.stop();
        this.start();
      }
    },
  );
}